import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil, KeyRound, X, Lock, Trash2, QrCode } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

interface Cashier {
  id: string
  full_name: string
  phone: string
  active: number
  last_login_at: string | null
  created_at: string
  created_by_name: string | null
}

const PIN_RE = /^\d{4,6}$/

export default function Cashiers() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const qc = useQueryClient()
  const [modal, setModal] = useState<Cashier | null | 'new'>(null)
  const [pinResetTarget, setPinResetTarget] = useState<Cashier | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Cashier | null>(null)

  const { data, isLoading } = useQuery<{ cashiers: Cashier[] }>({
    queryKey: ['cashiers'],
    queryFn: () => api.get('/cashiers').then(r => r.data),
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['cashiers'] })

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Lock size={20} className="text-[#c41e3a]" />
            Cashiers
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            POS register users. Sign in at the terminal with a <strong>login identifier</strong> (short staff code like <code className="text-[#c41e3a]">3</code> for fast entry, or a full phone number) plus a 4–6 digit PIN. No admin portal access.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setModal('new')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
          >
            <Plus size={16} />
            New cashier
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.cashiers ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No cashiers yet. Add one to start using the POS terminal.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Name</th>
                <th className="text-left px-5 py-3 font-medium" title="Short staff code for fast till entry, OR full phone number">Login</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Last login</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.cashiers ?? []).map(c => (
                <tr key={c.id} className="border-t border-white/10">
                  <td className="px-5 py-3 text-sm text-white font-medium">{c.full_name}</td>
                  <td className="px-5 py-3 text-sm text-gray-400 font-mono">{c.phone}</td>
                  <td className="px-5 py-3">
                    {c.active === 1 ? (
                      <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                    ) : (
                      <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Disabled</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">
                    {c.last_login_at ? formatDateTime(c.last_login_at) : <span className="text-gray-600">Never</span>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {canManage && (
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={async () => {
                            const res = await api.get(`/cashiers/${c.id}/badge.pdf`, { responseType: 'blob' })
                            const url = URL.createObjectURL(res.data as Blob)
                            window.open(url, '_blank')
                            // Revoke shortly after; the new tab has already loaded its bytes.
                            setTimeout(() => URL.revokeObjectURL(url), 30_000)
                          }}
                          className="p-1.5 text-gray-500 hover:text-white"
                          title="Print QR badge — cashier scans this on the till to auto-fill their login code"
                        >
                          <QrCode size={15} />
                        </button>
                        <button
                          onClick={() => setPinResetTarget(c)}
                          className="p-1.5 text-gray-500 hover:text-white"
                          title="Reset PIN"
                        >
                          <KeyRound size={15} />
                        </button>
                        <button
                          onClick={() => setModal(c)}
                          className="p-1.5 text-gray-500 hover:text-white"
                          title="Edit"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(c)}
                          className="p-1.5 text-gray-500 hover:text-red-400"
                          title="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal !== null && (
        <CashierModal
          cashier={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { refresh(); setModal(null) }}
        />
      )}

      {pinResetTarget && (
        <ResetPinModal
          cashier={pinResetTarget}
          onClose={() => setPinResetTarget(null)}
          onSaved={() => { refresh(); setPinResetTarget(null) }}
        />
      )}

      {deleteTarget && (
        <DeleteCashierModal
          cashier={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { refresh(); setDeleteTarget(null) }}
        />
      )}
    </div>
  )
}

function CashierModal({
  cashier,
  onClose,
  onSaved,
}: {
  cashier: Cashier | null
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = cashier === null
  const [fullName, setFullName] = useState(cashier?.full_name ?? '')
  const [phone, setPhone] = useState(cashier?.phone ?? '')
  const [pin, setPin] = useState('')
  const [active, setActive] = useState((cashier?.active ?? 1) === 1)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!fullName.trim()) { setError('Name is required'); return }
    if (isNew) {
      if (!phone.trim()) { setError('Login identifier is required'); return }
      if (!PIN_RE.test(pin)) { setError('PIN must be 4–6 digits'); return }
    }
    setSubmitting(true)
    try {
      if (isNew) {
        await api.post('/cashiers', {
          full_name: fullName.trim(),
          phone: phone.trim(),
          pin,
        })
      } else {
        await api.patch(`/cashiers/${cashier!.id}`, {
          full_name: fullName.trim(),
          active,
        })
      }
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? 'Couldn\'t save.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-[#1a1a1a] border border-white/10 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">
            {isNew ? 'Add cashier' : `Edit ${cashier?.full_name}`}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div>
          <label className="text-xs text-gray-500">Full name</label>
          <input
            type="text"
            value={fullName}
            onChange={e => setFullName(e.target.value)}
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]"
            autoFocus
          />
        </div>

        <div>
          <label className="text-xs text-gray-500">Login identifier</label>
          <input
            type="text"
            value={phone}
            onChange={e => isNew && setPhone(e.target.value)}
            disabled={!isNew}
            placeholder="e.g. 3 (short code) or +962 7XXXX XXXX"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm font-mono text-white focus:outline-none focus:border-[#c41e3a] disabled:opacity-50"
          />
          {isNew ? (
            <p className="text-xs text-gray-600 mt-1">
              What the cashier types on the till. Short numeric code (1–4 digits) makes shift changes fast; a full phone works too. Must be unique.
            </p>
          ) : (
            <p className="text-xs text-gray-600 mt-1">Login identifier can't be changed after creation.</p>
          )}
        </div>

        {isNew && (
          <div>
            <label className="text-xs text-gray-500">PIN (4–6 digits)</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 2468"
              className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm font-mono text-white tracking-widest focus:outline-none focus:border-[#c41e3a]"
            />
            <p className="text-xs text-gray-600 mt-1">Cashier enters this at the POS terminal. Hand it over verbally; not surfaced again after save.</p>
          </div>
        )}

        {!isNew && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="cashier_active"
              checked={active}
              onChange={e => setActive(e.target.checked)}
              className="accent-[#c41e3a]"
            />
            <label htmlFor="cashier_active" className="text-xs text-gray-400">
              Active (uncheck to disable login)
            </label>
          </div>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Saving…' : isNew ? 'Add cashier' : 'Save'}
          </button>
        </div>
      </form>
    </div>
  )
}

function ResetPinModal({
  cashier,
  onClose,
  onSaved,
}: {
  cashier: Cashier
  onClose: () => void
  onSaved: () => void
}) {
  const [pin, setPin] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!PIN_RE.test(pin)) { setError('PIN must be 4–6 digits'); return }
    setSubmitting(true)
    try {
      await api.post(`/cashiers/${cashier.id}/reset-pin`, { pin })
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? 'Couldn\'t reset PIN.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-[#1a1a1a] border border-white/10 rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Reset PIN for {cashier.full_name}</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div>
          <label className="text-xs text-gray-500">New PIN (4–6 digits)</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="e.g. 1357"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm font-mono text-white tracking-widest focus:outline-none focus:border-[#c41e3a]"
            autoFocus
          />
          <p className="text-xs text-gray-600 mt-1">Bumps token_version so any active session falls out on next request.</p>
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Saving…' : 'Reset PIN'}
          </button>
        </div>
      </form>
    </div>
  )
}

// Hard-delete confirmation. Worker rejects with 409 has_sales when the
// cashier has any orders.cashier_id / voided_by_cashier_id references —
// in that case we surface the typed message inline and tell the admin to
// deactivate instead (the standard offboarding path). Brand-new / test
// cashiers with zero history can be purged here.
function DeleteCashierModal({
  cashier,
  onClose,
  onDeleted,
}: {
  cashier: Cashier
  onClose: () => void
  onDeleted: () => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleDelete() {
    setSubmitting(true)
    setError(null)
    try {
      await api.delete(`/cashiers/${cashier.id}`)
      onDeleted()
    } catch (e: any) {
      setError(e.response?.data?.message ?? "Couldn't delete cashier.")
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-6 max-w-md w-full space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Delete {cashier.full_name}?</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed">
          This permanently removes the cashier and their PIN. Only allowed when the cashier has zero
          POS sales on record — otherwise the worker will reject the delete and you should{' '}
          <span className="text-white font-medium">deactivate</span> them instead (keeps shift history attributable).
        </p>
        <div className="bg-[#242424] border border-[#3a3a3a] rounded-lg p-3 text-xs text-gray-500 space-y-1">
          <div><span className="text-gray-400">Login:</span> <span className="font-mono text-white">{cashier.phone}</span></div>
          <div>
            <span className="text-gray-400">Last login:</span>{' '}
            <span className="text-white">{cashier.last_login_at ? formatDateTime(cashier.last_login_at) : 'Never'}</span>
          </div>
        </div>
        {error && <p className="text-xs text-red-400">{error}</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Deleting…' : 'Delete cashier'}
          </button>
        </div>
      </div>
    </div>
  )
}
